<script setup>
import { onMounted, ref, computed, watch } from "vue";
import { useUserStore } from "@/store/user-store";
import AnButton from "@/components/common/AnButton.vue";
import { Checkbox } from "primevue";
import AnInput from "@/components/common/AnInput.vue";
import Spinner from "@/components/common/AnSpinner.vue";

const store = useUserStore();

onMounted(() => {
  store.setAuthView("signin");
});

// Watch for dashboard loading completion to reset signin state
watch(() => store.isDashboardLoading, (isLoading, wasLoading) => {
  // When dashboard finishes loading (wasLoading true -> isLoading false)
  if (wasLoading && !isLoading && isSigningIn.value) {
    isSigningIn.value = false;
  }
});

const props = defineProps({
  authEnabled: {
    type: Boolean,
    default: false,
  },
  setAuth: {
    type: Function,
    required: true,
  },
});

const email = ref("");
const password = ref("");
const confirmPassword = ref("");
const username = ref("");
const verificationCode = ref("");
const resetCode = ref("");
const newPassword = ref("");
const confirmNewPassword = ref("");
const rememberMe = ref(false);
const acceptTerms = ref(false);
const passwordVisible = ref(false);
const confirmVisible = ref(false);
const isSigningIn = ref(false);

const isLoginButtonDisabled = computed(() => {
  return email.value.length < 2 || password.value.length < 2 || isSigningIn.value || store.isDashboardLoading;
});

const isAuthenticating = computed(() => {
  return isSigningIn.value || store.isDashboardLoading;
});

const signinController = async () => {
  if (isSigningIn.value || store.isDashboardLoading) return;

  isSigningIn.value = true;
  try {
    await store.userSignIn(email.value, password.value);
    // Keep button disabled until dashboard loads
    // isSigningIn will be reset when dashboard finishes loading or on error
  } catch (error) {
    // Reset immediately on error
    isSigningIn.value = false;
  }
};

const signUpController = () => {
  store.userSignUp(
    email.value,
    password.value,
    confirmPassword.value,
    username.value
  );
};

const verifySignupController = () => {
  store
    .userSignUpVerification(email.value, verificationCode.value)
    .then(() => resetAuthForm());
};

const forgotPasswordController = () => {
  store.handleForgotPassword(
    email.value,
    newPassword.value,
    confirmNewPassword.value,
    resetCode.value
  );
  store.forgotPasswordStep === "confirm" && resetAuthForm();
};

const resetCodeController = () => {
  store.resendPasswordResetCode(email.value);
};

const resetAuthForm = () => {
  password.value = "";
  confirmPassword.value = "";
  username.value = "";
  verificationCode.value = "";
  resetCode.value = "";
  newPassword.value = "";
  confirmNewPassword.value = "";
  rememberMe.value = false;
  acceptTerms.value = false;
  passwordVisible.value = false;
  confirmVisible.value = false;
  isSigningIn.value = false;
};
</script>

<template>
  <div v-if="props.authEnabled">
    <Spinner />
    <div
      class="auth-overlay pt-40 ph-20 h-fs w-fs d-flex justify-center"
      @click="
        () => {
          setAuth(false), store.setAuthView('signin');
        }
      "
    >
      <div class="auth-container h-max p-30" @click.stop>
        <div class="auth-wrapper">
          <!-- Auth Tab Titles -->
          <div class="font-35 font-weight-700 d-flex justify-center mb-10">
            <span v-if="store.currentAuthView === 'signin'">Login</span>
            <span v-else-if="store.currentAuthView === 'signup'">Sign Up</span>
            <span
              v-else-if="
                store.currentAuthView === 'forgot-password' &&
                store.forgotPasswordStep === 'request'
              "
              >Reset Password</span
            >
            <span
              v-else-if="
                store.currentAuthView === 'forgot-password' &&
                store.forgotPasswordStep === 'confirm'
              "
              >Confirm Reset Code</span
            >
            <span v-else-if="store.currentAuthView === 'verify-signup'"
              >Verify Your Account</span
            >
          </div>
          <!-- Login Form -->
          <form
            v-if="store.currentAuthView === 'signin'"
            @submit.prevent="signinController"
            class="d-flex flex-direction-column"
          >
            <AnInput
              type="email"
              v-model="email"
              required
              placeholder="Enter your email"
            />
            <AnInput
              v-model="password"
              required
              placeholder="Enter your password"
              :iconClickable="true"
              :icon="passwordVisible ? 'pi pi-eye-slash' : 'pi pi-eye'"
              @onIconClick="() => (passwordVisible = !passwordVisible)"
              :type="passwordVisible ? 'text' : 'password'"
            />
            <div class="d-flex justify-between mt-20">
              <div class="d-flex gap-5 align-items-center">
                <Checkbox
                  inputId="remember-me"
                  name="Remember me"
                  value="remember-me"
                  v-model="rememberMe"
                  class="checkbox d-lex justify-center"
                />
                <label for="remember-me" class="font-weight-600">
                  Remember Me
                </label>
              </div>
              <span
                class="font-weight-600 cursor-pointer"
                @click="
                  () => {
                    store.forgotPasswordStep = 'request';
                    store.setAuthView('forgot-password');
                    resetAuthForm();
                  }
                "
              >
                Forgot Password?
              </span>
            </div>
            <div class="mt-40 d-flex justify-center">
              <AnButton
                type="submit"
                variant="secondary"
                :label="isAuthenticating ? 'Signing In...' : 'Login'"
                button-class="submit-btn d-flex gap-10"
                :icon="isAuthenticating ? 'pi pi-spin pi-spinner' : 'pi pi-sign-in'"
                icon-position="left"
                :disabled="isLoginButtonDisabled"
              />
            </div>

            <div class="mt-30 text-center">
              <span>
                Don't have a account?
                <span
                  class="font-weight-700 cursor-pointer"
                  @click="
                    () => {
                      store.setAuthView('signup');
                      resetAuthForm();
                    }
                  "
                  >Register</span
                >
              </span>
            </div>
          </form>
          <!-- Sign Up form -->
          <form
            v-if="store.currentAuthView === 'signup'"
            @submit.prevent="signUpController"
            class="d-flex flex-direction-column"
          >
            <AnInput
              type="text"
              v-model="username"
              required
              placeholder="Enter your First Name"
            />

            <AnInput
              type="email"
              v-model="email"
              required
              placeholder="Enter your email"
            />

            <AnInput
              v-model="password"
              required
              placeholder="Enter your password"
              :iconClickable="true"
              :icon="passwordVisible ? 'pi pi-eye-slash' : 'pi pi-eye'"
              @onIconClick="() => (passwordVisible = !passwordVisible)"
              :type="passwordVisible ? 'text' : 'password'"
            />

            <AnInput
              v-model="confirmPassword"
              required
              placeholder="Confirm your password"
              :iconClickable="true"
              :icon="confirmVisible ? 'pi pi-eye-slash' : 'pi pi-eye'"
              @onIconClick="() => (confirmVisible = !confirmVisible)"
              :type="confirmVisible ? 'text' : 'password'"
            />

            <div class="d-flex mt-20 gap-5 align-items-center">
              <Checkbox
                inputId="acceptTerms"
                name="acceptTerms"
                value="acceptTerms"
                v-model="acceptTerms"
                required
                class="checkbox d-lex justify-center"
              />
              <label for="acceptTerms">
                I Agree With Terms Of Asset Nivesh
              </label>
            </div>
            <div class="mt-40 d-flex justify-center">
              <AnButton
                type="submit"
                variant="secondary"
                label="Sign Up"
                button-class="submit-btn d-flex gap-10"
                icon="pi pi-sign-in"
                icon-position="left"
              />
            </div>
            <div class="mt-30 text-center">
              <span>
                Already have a account?
                <span
                  class="font-weight-700 cursor-pointer"
                  @click="
                    () => {
                      store.setAuthView('signin');
                      resetAuthForm();
                    }
                  "
                  >Login</span
                >
              </span>
            </div>
          </form>
          <!-- Verify Signup -->
          <form
            v-if="store.currentAuthView === 'verify-signup'"
            @submit.prevent="verifySignupController"
          >
            <div class="text-start">
              <span>
                A verification code has been sent to <b>{{ email }}</b
                >. Please check your inbox (and spam folder) and enter the code
                below.
              </span>
            </div>
            <AnInput
              type="text"
              v-model="verificationCode"
              required
              placeholder="Enter the 6-digit code"
              maxlength="6"
            />
            <div class="d-flex justify-center mt-40">
              <AnButton
                type="submit"
                variant="secondary"
                label="Verify Account"
                class="w-max"
              />
            </div>
            <div class="mt-30 text-center">
              <span
                @click="store.resendSignUpVerificationCode(email)"
                class="cursor-pointer font-weight-700"
              >
                Resend Verification Code
              </span>
            </div>
          </form>
          <!-- Forgot Password Section -->
          <form
            v-if="store.currentAuthView === 'forgot-password'"
            @submit.prevent="forgotPasswordController"
            class="d-flex flex-direction-column"
          >
            <template v-if="store.forgotPasswordStep === 'request'">
              <AnInput
                type="email"
                v-model="email"
                required
                placeholder="Enter your email"
              />
              <div class="d-flex justify-center mt-40">
                <AnButton
                  type="submit"
                  variant="secondary"
                  label="Send Reset Code"
                  class="w-max"
                />
              </div>
              <div class="mt-30 text-center">
                <span
                  @click="
                    () => {
                      store.setAuthView('signin');
                      resetAuthForm();
                    }
                  "
                  class="cursor-pointer font-weight-700"
                >
                  Back to Sign In
                </span>
              </div>
            </template>
            <template v-else-if="store.forgotPasswordStep === 'confirm'">
              <AnInput
                type="text"
                v-model="resetCode"
                required
                maxlength="6"
                placeholder="Enter reset code"
              />
              <AnInput
                v-model="newPassword"
                required
                placeholder="Enter new password"
                :iconClickable="true"
                :icon="passwordVisible ? 'pi pi-eye-slash' : 'pi pi-eye'"
                @onIconClick="() => (passwordVisible = !passwordVisible)"
                :type="passwordVisible ? 'text' : 'password'"
              />
              <AnInput
                v-model="confirmNewPassword"
                required
                placeholder="Confirm new password"
                :iconClickable="true"
                :icon="confirmVisible ? 'pi pi-eye-slash' : 'pi pi-eye'"
                @onIconClick="() => (confirmVisible = !confirmVisible)"
                :type="confirmVisible ? 'text' : 'password'"
              />

              <div class="d-flex justify-center mt-40">
                <AnButton
                  type="submit"
                  variant="secondary"
                  label="Reset Password"
                  class="w-max"
                />
              </div>
              <div class="mt-30 text-center">
                <span
                  @click="resetCodeController"
                  class="cursor-pointer font-weight-700"
                >
                  Resend Reset Code
                </span>
              </div>
            </template>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.auth-overlay {
  background: var(--an-auth-overlay);
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  overflow-y: auto;
}

.auth-container {
  color: var(--an-white);
  width: 530px;
  background: var(--an-auth-background);
  border-radius: 16px;
  backdrop-filter: blur(15px);
  z-index: 10000;
}

.submit-btn {
  width: 270px;
}

.checkbox {
  height: 16px;
  width: 16px;
  border: 1px solid var(--an-white);
  border-radius: 4px;
}
</style>
